// =======================================================
// 📄 src/LandLordPages/Settings.jsx
// =======================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Camera,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  CheckCircle2,
  X,
  RefreshCw,
  ShieldCheck,
  Info,
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  sendOtpForPasswordReset,
  verifyOtpForPasswordReset,
  landlordResetPassword,
} from "../api/LandlordDashboard";

// ---------- Helpers ----------
const capitalize = (s = "") =>
  s
    .split(" ")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const normalizeErr = (err, fallback) =>
  String(
    err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.message ||
      fallback
  );

const maskEmail = (email = "") => {
  const [u, d] = email.split("@");
  if (!u || !d) return email || "—";
  const keep = Math.min(2, u.length);
  return `${u.slice(0, keep)}***@${d}`;
};

const pwChecks = [
  { id: "len8", label: "At least 8 chars", test: (v) => v.length >= 8 },
  { id: "lower", label: "Lowercase", test: (v) => /[a-z]/.test(v) },
  { id: "upper", label: "Uppercase", test: (v) => /[A-Z]/.test(v) },
  { id: "num", label: "Number", test: (v) => /\d/.test(v) },
  { id: "sym", label: "Symbol", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function Settings() {
  // Profile
  const [email, setEmail] = useState("");
  const [name, setName] = useState("User");
  const [avatarPreview, setAvatarPreview] = useState(null);

  // OTP modal
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpLoading, setOtpLoading] = useState(false);
  const otpInputsRef = useRef([]);
  const [resendIn, setResendIn] = useState(0);
  const resendTimerRef = useRef(null);

  // UI
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState("");
  const [toast, setToast] = useState("");

  const readItem = (k) => localStorage.getItem(k) ?? sessionStorage.getItem(k);

  const readJSON = (k, fb = null) => {
    try {
      const v = readItem(k);
      return v ? JSON.parse(v) : fb;
    } catch {
      return fb;
    }
  };
  useEffect(() => {
    const storedEmail =
      readItem("user_email") || readJSON("user", {})?.email || "";

    setEmail(storedEmail);

    const baseName = storedEmail ? storedEmail.split("@")[0] : "User";
    setName(capitalize(baseName.replace(/[._-]/g, " ")));

    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const avatarInitial = useMemo(
    () => (name?.charAt(0) || "U").toUpperCase(),
    [name]
  );

  // Formik: change password
  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: Yup.object({
      newPassword: Yup.string().min(8, "Min 8 characters").required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (_values, { setSubmitting }) => {
      setStatus("");
      if (!email) {
        setStatus("Missing email for this account.");
        setSubmitting(false);
        return;
      }
      try {
        await sendOtpForPasswordReset(email /*, "password_reset" */);
        openOtpModalFresh();
      } catch (err) {
        setStatus(normalizeErr(err, "Failed to send OTP."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Password strength
  const score = pwChecks.reduce(
    (acc, c) => acc + (c.test(formik.values.newPassword) ? 1 : 0),
    0
  );
  const strength = score <= 2 ? "Weak" : score <= 4 ? "Medium" : "Strong";
  const strengthBar = Array.from({ length: 5 }, (_, i) => i < score);

  // Avatar (optional)
  const onPickAvatar = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
  };
  const clearAvatar = () => setAvatarPreview(null);

  // OTP handlers
  const openOtpModalFresh = () => {
    setOtp(Array(6).fill(""));
    setOtpOpen(true);
    setTimeout(() => otpInputsRef.current[0]?.focus(), 0);
    startResend(30); // cooldown = 30s
  };

  const startResend = (sec) => {
    setResendIn(sec);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const resendCode = async () => {
    if (!email || resendIn > 0) return;
    try {
      await sendOtpForPasswordReset(email);
      startResend(30);
    } catch (err) {
      setStatus(normalizeErr(err, "Failed to resend OTP."));
    }
  };

  const onOtpChange = (val, idx) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) otpInputsRef.current[idx + 1]?.focus();
  };
  const onOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpInputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") {
      if (otp.join("").length === 6 && !otpLoading) verifyOtpAndReset();
    }
  };
  const onOtpPaste = (e) => {
    const text = e.clipboardData?.getData("text")?.trim() || "";
    if (/^\d{6}$/.test(text)) {
      e.preventDefault();
      setOtp(text.split(""));
      setTimeout(() => otpInputsRef.current[5]?.focus(), 0);
    }
  };

  const closeOtpModal = () => {
    setOtpOpen(false);
    setOtp(Array(6).fill(""));
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendIn(0);
  };

  // Verify + reset
  const verifyOtpAndReset = async () => {
    const code = otp.join("");
    if (code.length !== 6) return;
    setOtpLoading(true);
    setStatus("");
    try {
      await verifyOtpForPasswordReset(email, code);
      await landlordResetPassword({
        email,
        otp: code,
        new_password: formik.values.newPassword,
      });

      // ✅ Log out user globally after password change
      setToast("Password changed successfully. Logging out…");
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/landlord-signin";
      }, 1500);
    } catch (err) {
      setStatus(normalizeErr(err, "Invalid code or reset failed."));
    } finally {
      setOtpLoading(false);
    }
  };

  const logoutAndRedirect = (delay = 1500) => {
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("user");
      localStorage.removeItem("user_email");

      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("token_type");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("user_email");
    } catch {}

    setTimeout(() => {
      window.location.replace("/");
    }, delay);
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden p-4 sm:p-6 lg:p-8">
      {/* Background cosmetics */}
      <motion.div
        className="pointer-events-none absolute -top-24 -left-16 h-[420px] w-[420px] rounded-full bg-blue-400/30 blur-3xl"
        animate={{ x: [0, 16, 0], y: [0, 24, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-28 -right-10 h-[360px] w-[360px] rounded-full bg-blue-400/30 blur-3xl"
        animate={{ x: [0, -16, 0], y: [0, -22, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Centered heading (navbar stays from LandlordLayout) */}
      <div className="mx-auto max-w-4xl text-center">
        <div className="inline-flex items-center gap-3 rounded-2xl border bg-white/70 backdrop-blur px-4 py-2 shadow">
          <ShieldCheck className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg sm:text-xl font-semibold">Account Settings</h1>
        </div>
        <p className="mt-2 text-xs sm:text-sm text-gray-500">
          Manage your profile and keep your account secure
        </p>
      </div>

      {/* Content grid */}
      <div className="mx-auto mt-6 grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-[1fr_1.3fr]">
        {/* Profile card */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-2xl border bg-white/80 backdrop-blur p-6 shadow"
        >
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="grid h-20 w-20 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white text-3xl font-semibold shadow">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar"
                    className="h-20 w-20 rounded-2xl object-cover"
                  />
                ) : (
                  avatarInitial
                )}
              </div>

              <div className="absolute -bottom-2 -right-2 flex gap-1"></div>
            </div>

            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold break-words">
                {name}
              </div>
              <div className="mt-1 inline-flex items-center gap-2 rounded-xl border bg-gray-50 px-3 py-1 text-xs text-gray-600">
                <Mail className="h-3.5 w-3.5" />
                <span className="break-all">{email || "—"}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-center text-xs text-gray-600">
            <div className="rounded-xl border bg-white px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">
                Status
              </div>
              <div className="mt-1 font-medium">Active</div>
            </div>
            <div className="rounded-xl border bg-white px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">
                2FA
              </div>
              <div className="mt-1 font-medium">Email OTP</div>
            </div>
          </div>
        </motion.div>

        {/* Change password card */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          className="rounded-2xl border bg-white/80 backdrop-blur p-6 shadow"
        >
          <div className="flex items-center justify-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-600" />
            <h2 className="text-base sm:text-lg font-semibold">
              Change Password
            </h2>
          </div>

          <form onSubmit={formik.handleSubmit} className="mt-5 space-y-4">
            {/* Inline status */}
            {status && (
              <div
                className={`text-sm rounded-xl border px-3 py-2 text-center ${
                  status.startsWith("✅")
                    ? "text-green-700 bg-green-50 border-green-200"
                    : "text-red-700 bg-red-50 border-red-200"
                }`}
              >
                {status}
              </div>
            )}

            {/* New password */}
            <div>
              <label className="text-sm font-medium">New Password</label>
              <div className="relative mt-1">
                <input
                  type={showPw ? "text" : "password"}
                  name="newPassword"
                  placeholder="Minimum 8 characters"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-xl border px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                  aria-invalid={
                    formik.touched.newPassword && formik.errors.newPassword
                      ? "true"
                      : "false"
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {formik.touched.newPassword && formik.errors.newPassword && (
                <p className="mt-1 text-xs text-red-600">
                  {formik.errors.newPassword}
                </p>
              )}

              {/* Strength meter + requirements */}
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  {strengthBar.map((on, i) => (
                    <div
                      key={i}
                      className={`h-1.5 w-full rounded-full ${
                        on ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  Strength: <span className="font-medium">{strength}</span>
                </div>
                <ul className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-gray-600">
                  {pwChecks.map((c) => {
                    const ok = c.test(formik.values.newPassword);
                    return (
                      <li
                        key={c.id}
                        className={`inline-flex items-center gap-1 ${
                          ok ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            ok ? "bg-green-600" : "bg-gray-300"
                          }`}
                        />
                        {c.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="text-sm font-medium">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Re-enter new password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                aria-invalid={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                    ? "true"
                    : "false"
                }
              />
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    {formik.errors.confirmPassword}
                  </p>
                )}
            </div>

            {/* Info note */}
            <div className="flex items-start gap-2 text-[11px] text-gray-500">
              <Info className="h-3.5 w-3.5 mt-0.5" />
              <p>
                We’ll send a verification code to your email before applying
                changes.
              </p>
            </div>

            <div className="pt-1">
              <button
                type="submit"
                disabled={formik.isSubmitting || !email}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow hover:shadow-md disabled:opacity-50"
              >
                {formik.isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending code…
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
              <p className="mt-2 text-[11px] text-center text-gray-500">
                Code will be sent to{" "}
                <span className="font-medium">{maskEmail(email)}</span>.
              </p>
            </div>
          </form>
        </motion.div>
      </div>

      {/* OTP Modal */}
      <AnimatePresence>
        {otpOpen && (
          <motion.div
            className="fixed inset-0 z-50 grid place-items-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={closeOtpModal}
            />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="relative w-full max-w-md rounded-3xl border bg-gradient-to-br from-white/85 to-white/60 p-6 sm:p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Verify your email"
            >
              <button
                onClick={closeOtpModal}
                className="absolute right-4 top-4 rounded-xl p-2 text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-600 text-white shadow">
                  <KeyRound className="h-6 w-6" />
                </div>
                <h3 className="mt-3 text-lg sm:text-xl font-semibold">
                  Enter verification code
                </h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-600">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium">{maskEmail(email)}</span>
                </p>
              </div>

              <div className="mt-6 flex justify-center gap-2 sm:gap-3">
                {otp.map((d, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (otpInputsRef.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => onOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => onOtpKeyDown(e, idx)}
                    onPaste={idx === 0 ? onOtpPaste : undefined}
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl border bg-white/80 text-center text-lg font-semibold outline-none focus:ring-2 focus:ring-blue-300"
                    aria-label={`Digit ${idx + 1}`}
                  />
                ))}
              </div>

              {/* Inline error from verify/resend */}
              {status && (
                <div className="mt-4 text-center text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {status}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={resendIn > 0}
                  className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
                <span>Didn’t get it? Check spam.</span>
              </div>

              <button
                onClick={verifyOtpAndReset}
                disabled={otp.join("").length !== 6 || otpLoading}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow hover:shadow-md disabled:opacity-50"
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify & Change Password"
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2"
          >
            <div className="inline-flex items-center gap-2 rounded-2xl border bg-white/90 px-4 py-2 shadow-lg backdrop-blur">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">{toast}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
